import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Button, Container, Spinner } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { matchApi, markupApi, eventsApi } from "@/api";
import Preloader from "@/components/Elements/Preloader";
import VideoPlayer from "@/components/VideoPlayer";
import EditCell from "./EditCell";
import EditableCell from "./EditableCell";
import EventsTable from "./EventsTable";
import { convertTime2Frame, frameToTime } from "@/util/functions";
import "./Validation.scss";
import SubEventsCell from "./SubEventsCell";
import { LABELER } from "@/constants/routs.constants";
import { Arrow } from "react-konva";
import Switch from "@/components/Elements/switch";
import EventControls from "./EventControls";
import EventButton from "./EventButton";
import { transform } from "lodash";

const columnHelper = createColumnHelper();

const Events = () => {
  const navigate = useNavigate();
  const { videoId, trackId } = useParams();
  const location = useLocation();

  // --- Refs ---
  const videoRef = useRef(null);
  const tableRef = useRef(null);
  const newArrowRef = useRef();
  const new2DArrowRef = useRef();
  const newArrow = useRef({
    visible: false,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  });
  const new2DArrow = useRef({
    visible: false,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  });

  // --- State: Video/Field ---
  const [fps, setFps] = useState(1);
  const [videoUrl, setVideoUrl] = useState("");
  const [fieldWidth, setFieldWidth] = useState(0);
  const [fieldHeight, setFieldHeight] = useState(0);
  const [videoStartTime, setVideoStartTime] = useState(null);
  const [videoEndTime, setVideoEndTime] = useState(null);
  const [videoDomHeight, setVideoDomHeight] = useState(0);

  // --- State: Events ---
  const [events, setEvents] = useState([]);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [realCurrentEvents, setRealCurrentEvents] = useState([]);
  const [tableEvents, setTableEvents] = useState([]);
  const [subEvents, setSubEvents] = useState({});
  const [subEventsListData, setSubEventsListData] = useState({
    isOpen: false,
    visibleSubEvents: [],
    onSelectEvent: null,
    position: { x: 10, y: 10 },
    eventType: null,
  });

  // --- State: UI/Editing ---
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState(null);

  // --- State: Table/Selection ---
  const [supertracks, setSupertracks] = useState({});
  const [supertrackUids, setSupertrackUids] = useState([]);
  const [matchSuperTracks, setMatchSuperTracks] = useState([]);
  const [activeTracks, setActiveTracks] = useState([]);
  const [superTrackUid, setSuperTrackUid] = useState(null);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [selectionMode, setSelectionMode] = useState("active");
  const [eventSelected, setEventSelected] = useState(false);

  // --- State: Intervals/Zoom/Other ---
  const [intervals, setIntervals] = useState([]);
  const [frameStep, setFrameStep] = useState(1);
  const [autoZoom, setAutoZoom] = useState(false);
  const [watchMoreMode, setWatchMoreMode] = useState(false);
  const [stageScale, setStageScale] = useState(1);
  const [stageX, setStageX] = useState(0);
  const [stageY, setStageY] = useState(0);
  const [ratio, setRatio] = useState(1);
  const [initialY, setInitialY] = useState(0);

  // --- State: Task/Teams ---
  const [taskId, setTaskId] = useState(null);
  const [isManaging, setIsManaging] = useState(true);
  const [teamsAmount, setTeamsAmount] = useState(null);
  const [opponentsSuperTrack, setOpponentsSuperTrack] = useState(null);
  const [isSingleTeam, setIsSingleTeam] = useState(false);
  const [lightMode, setLightMode] = useState(false);
  const [pipelineType, setPipelineType] = useState(null);
  const [trainingPartData, setTrainingPartData] = useState(null);
  const [trainingZoomArea, setTrainingZoomArea] = useState(null);

  // Function to convert training part zone to zoom area
  const convertZoneToZoomArea = useCallback((zone) => {
    if (!zone || zone.length < 2) return null;

    // Find min and max coordinates to create a bounding box
    const xCoords = zone.map((point) => point.x);
    const yCoords = zone.map((point) => point.y);

    const x1 = Math.min(...xCoords);
    const x2 = Math.max(...xCoords);
    const y1 = Math.min(...yCoords);
    const y2 = Math.max(...yCoords);

    return { x1, x2, y1, y2 };
  }, []);

  // --- State: Preview/Start-End ---
  const [previewEventId, setPreviewEventId] = useState(null);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [showBoxes, setShowBoxes] = useState(true); // Add new state for show boxes
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        filterFn: "idFilterFn",
      },
      {
        accessorKey: "super_track",
        header: "Active Player",
        cell: (props) => {
          return (
            <EditableCell
              {...props}
              fieldName="super_track_uid"
              fieldType="number"
            />
          );
        },
        meta: {
          filterVariant: "select",
          type: "select",
          options: supertrackUids.map((uid) => ({ label: uid, value: uid })),
        },
        filterFn: "activePlayerFilterFn",
      },
      {
        accessorKey: "extra",
        header: "Passive Player",
        cell: (props) => {
          return (
            <EditableCell {...props} fieldName="player" fieldType="number" />
          );
        },
        meta: {
          filterVariant: "select",
          type: "select",
          options: [
            { label: "None", value: null },
            ...supertrackUids.map((uid) => ({ label: uid, value: uid })),
          ],
        },
        filterFn: "passivePlayerFilterFn",
      },
      {
        accessorKey: "event_type",
        header: "Type",
        cell: EditableCell,
        meta: {
          filterVariant: "select",
          type: "select",
          options: [
            {
              label: "_Pass",
              value: "pass",
            },
            {
              label: "_Recovery",
              value: "recovery",
            },
            {
              label: "_Interception",
              value: "interception",
            },
            {
              label: "_Dribble",
              value: "dribble",
            },
            {
              label: "_Tackle",
              value: "tackle",
            },
            {
              label: "_Shot",
              value: "shot",
            },
            {
              label: "_Duel",
              value: "duel",
            },
            {
              label: "_Technical Mistake",
              value: "technical_mistake",
            },
            {
              label: "_Entries in penalty area",
              value: "entries_in_penalty_area",
            },
            {
              label: "_Foul",
              value: "foul",
            },
            {
              label: "_Offside",
              value: "offside",
            },
            {
              label: "_Save",
              value: "save",
            },
            {
              label: "_Auto goal",
              value: "auto_goal",
            },
            {
              label: "_Goalkeeper Exit",
              value: "goalkeeper_exit",
            },
          ],
        },
      },
      {
        accessorKey: "success",
        header: "Result",
        cell: EditableCell,
        meta: {
          type: "select",
          options: [
            { label: "_Success", value: true },
            { label: "_Fail", value: false },
          ],
          filterVariant: "select",
        },
        filterFn: "resultFilterFn",
      },
      columnHelper.accessor((row) => row.super_track.kind, {
        id: "kind",
        cell: (props) => (
          <>
            {props.getValue() === "right_team"
              ? "Right"
              : props.getValue() === "left_team"
              ? "Left"
              : "Unknown"}
          </>
        ),
        header: "Team",
        meta: {
          type: "select",
          filterVariant: "select",
          options: [
            { label: "_Right", value: "right_team" },
            { label: "_Left", value: "left_team" },
          ],
        },
        filterFn: "kindFilterFn",
      }),
      columnHelper.accessor((row) => row.extra.additional_events, {
        id: "additional_events",
        cell: (props) => (
          <SubEventsCell {...props} fieldName="additional_events" />
        ),
        header: "Additional Events",
        meta: {
          subEvents: subEvents,
        },
        filterFn: "subEventsFilterFn",
      }),
      {
        accessorKey: "extra",
        id: "Start Time",
        header: "Start Time",
        cell: (props) => {
          return (
            <EditableCell
              {...props}
              fieldName="current_frame"
              converter={(value) => frameToTime(value, fps)}
            />
          );
        },
        meta: {
          type: "text",
        },
        filterFn: "startTimeFilterFn",
      },
      {
        accessorKey: "extra",
        id: "End Time",
        header: "End Time",
        cell: (props) => {
          return <EditableCell {...props} fieldName="event_end" />;
        },
        meta: {
          type: "text",
        },
        filterFn: "endTimeFilterFn",
      },
      {
        id: "Actions",
        header: "Actions",
        cell: EditCell,
        meta: {
          setSubEventsListData: setSubEventsListData,
        },
      },
    ],
    [supertrackUids, subEvents, fps, setSubEventsListData]
  );

  const addNewEvent = async (
    selectedEvent,
    selectedOptions,
    multipleSelections,
    callback
  ) => {
    try {
      // Get the current time from the video player for end time
      const currentTime = videoRef.current.currentTime;

      // Use the start time that was captured when the event was first selected
      // Fall back to current time if startTime wasn't set
      const eventStartTime = window.startTime || currentTime;

      const startTimeFrame = Math.round(eventStartTime * fps);
      const startTimeStamp = frameToTime(startTimeFrame, fps);

      // Use current time as the end time
      const endTimeFrame = Math.round(currentTime * fps);
      const endTimeStamp = frameToTime(endTimeFrame, fps);

      const videoStartTimeFrame = startTimeFrame - fps;
      const videoEndTimeFrame = endTimeFrame + fps * 2;

      // Calculate video start/end times based on the event start/end
      const videoStartTimeStamp = frameToTime(videoStartTimeFrame, fps);
      const videoEndTimeStamp = frameToTime(videoEndTimeFrame, fps);

      const transformedOptions = {
        ...Object.entries(selectedOptions)
          .filter(([key]) => {
            if (selectedEvent === "duel") {
              return key === "initial" ? false : true;
            }
            // Exclude packing from the generic reduction, handle below
            return !["initial", "success", "fail", "packing"].includes(key);
          })
          .reduce((acc, [key, value]) => {
            acc[value] = true;
            return acc;
          }, {}),
        ...Array.from(multipleSelections).reduce((acc, value) => {
          acc[value] = true;
          return acc;
        }, {}),
        // Special case: if packing is selected, add packing: Number(selectedOptions.packing)
        ...(selectedOptions.packing
          ? { packing: Number(selectedOptions.packing) }
          : {}),
      };

      const success = selectedOptions.initial === "success";

      if (superTrackUid === null) {
        console.log(
          "Cannot add event - superTrackUid is null in managing mode"
        );
        toast.warning("Please select an active player.");
        return;
      }

      console.log("Adding new event with:", {
        superTrackUid,
        selectedTrackId,
        isManaging,
      });

      if (
        !lightMode &&
        selectedEvent === "pass" &&
        success &&
        selectedTrackId === null
      ) {
        toast.warning("Please select a passive player for the pass event.");
        return;
      }

      if (!lightMode && selectedEvent === "duel" && selectedTrackId === null) {
        toast.warning("Please select a passive player for the duel event.");
        return;
      }

      // This clears the status of EventControls
      callback();

      const super_track_uid = superTrackUid ?? supertrackUids[0];
      const superTrack = matchSuperTracks[super_track_uid];
      const kind = superTrack?.kind;

      console.log("add: trainingPartData", trainingPartData);
      let payload = {
        event_type: selectedEvent,
        extra: {
          zoom: {
            x1: 0,
            x2: fieldWidth,
            y1: 0,
            y2: fieldHeight,
          },
          additional_events: transformedOptions,
          current_frame: startTimeFrame,
          event_end: endTimeStamp,
          player: selectedTrackId ? selectedTrackId : null,
          video_end: videoEndTimeStamp,
          video_start: videoStartTimeStamp,
          training_part_id: trainingPartData?.id || null,
        },
        success,
        super_track: {
          super_track_uid,
          kind,
        },
        timestamp: startTimeStamp,
      };

      console.log({
        transformedOptions,
        selectedOptions,
        multipleSelections,
        payload,
      });
      if (selectedEvent === "pass") {
        if (transformedOptions.forward) {
          payload.extra.additional_events.direction = 1;
          delete payload.extra.additional_events.forward;
        } else if (transformedOptions.back) {
          payload.extra.additional_events.direction = -1;
          delete payload.extra.additional_events.back;
        } else if (transformedOptions.sideway) {
          payload.extra.additional_events.direction = 0;
          delete payload.extra.additional_events.sideway;
        }
      } else if (selectedEvent === "ball_control") {
        if (selectedOptions?.initial === "tech_mistake") {
          payload.event_type = "technical_mistake";
        } else if (selectedOptions?.initial === "in_pen_area") {
          payload.event_type = "entries_in_penalty_area";
        }
      } else if (selectedEvent === "game_stops") {
        if (selectedOptions?.initial === "offside") {
          payload.event_type = "offside";
        } else if (selectedOptions?.initial === "foul") {
          payload.event_type = "foul";
        }
      } else if (selectedEvent === "interception") {
        payload.success = true;
      } else if (selectedEvent === "recovery") {
        payload.success = false;
        payload.extra.player = null;
      } else if (selectedEvent === "save") {
        payload.event_type = "shot";
        payload.success = true;
        payload.extra.additional_events = {
          save: true,
        };
      } else if (selectedEvent === "success_duel") {
        payload.event_type = "duel";
        payload.success = true;
      }

      console.log({ superTrackUid });

      const uid = superTrackUid;

      setSuperTrackUid(null);
      setSelectedTrackId(null);
      setSelectionMode("active");
      // After successfully adding the event, reset the event selected state
      setEventSelected(false);
      // Reset the start time after using it
      delete window.startTime;

      const { super_track_id, ...createdEvent } = await markupApi.addEvent(
        videoId,
        uid ?? supertrackUids[0],
        payload
      );

      console.log({ createdEvent });

      const sortedEvents = [
        {
          ...createdEvent,
          super_track: {
            super_track_uid,
            kind,
            id: super_track_id,
          },
        },
        ...events,
      ].sort((a, b) => a?.extra.current_frame - b?.extra.current_frame);
      setEvents(sortedEvents);

      toast.success("Successfully added new event");
    } catch (err) {
      console.log(err);
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        err.message ||
        "Something went wrong.";
      toast.error(errorMessage);

      // Also reset event selected state on error
      setEventSelected(false);
    }
  };

  const onFrameChange = useCallback(
    (currentFrame) => {
      // Original event filtering logic
      const events1 = [],
        events2 = [];
      for (let i = 0; i < events.length; i++) {
        const event = events[i];
        const {
          timestamp,
          extra: { event_end, video_start, video_end },
        } = event;
        const startFrameNumber = convertTime2Frame(timestamp, fps);
        const endFrameNumber = convertTime2Frame(event_end, fps);
        const videoStartFrameNumber = convertTime2Frame(video_start, fps);
        const videoEndFrameNumber = convertTime2Frame(video_end, fps);

        if (
          currentFrame >= videoStartFrameNumber &&
          currentFrame <= videoEndFrameNumber
        ) {
          if (
            currentFrame >= startFrameNumber &&
            currentFrame <= endFrameNumber
          ) {
            event.highlighted = true;
            events2.push(event);
          } else {
            event.highlighted = false;
          }
          events1.push(event);
        }
      }
      setCurrentEvents([...events1]);
      setRealCurrentEvents([...events2]);
    },
    [events, fps, intervals, watchMoreMode]
  );

  const onVideoDomSizeChange = useCallback((width, height) => {
    setVideoDomHeight(height);
  }, []);

  const turnOffPreviewMode = () => {
    setPreviewEventId(null);
  };

  const saveAnswer = async () => {
    const result = window.confirm(
      `Are you sure you want to complete validation of this match?`
    );

    if (result) {
      try {
        setIsSaving(true);
        const query = new URLSearchParams(location.search);
        const taskId = query.get("taskId");
        if (!taskId) {
          toast.warning("taskId is not specificed.");
          return;
        }

        await matchApi.postMatchTaskResult({
          matchId: videoId,
          taskId: taskId,
          taskResult: {
            status: true,
          },
          taskType: "event_tagging",
        });
        toast.success("Saved the answer successfully.");
        navigate(LABELER);
      } catch (err) {
        if (err.response?.status === 409) {
          toast.error(
            "This task has already been completed. You cannot submit it again."
          );
        } else {
          const errorMessage =
            err.response?.data?.message ||
            JSON.stringify(err.response?.data)?.slice(0, 200) ||
            err.message ||
            "Something went wrong while saving the answer.";
          toast.error(errorMessage);
        }
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleAddNewEvent = () => {
    const currentTime = Date.now();

    const startTimeFrame = Math.round(startTime * fps);
    const startTimeStamp = frameToTime(startTimeFrame, fps);
    const videoStartTimeStamp = frameToTime(
      Math.round(startTime * fps - 3 * fps),
      fps
    );
    const endTimeStamp = frameToTime(Math.round(endTime * fps), fps);
    const videoEndTimeStamp = frameToTime(
      Math.round(endTime * fps + 2 * fps),
      fps
    );

    setNewEvent({
      id: currentTime,
      event_type: "pass",
      extra: {
        zoom: {
          x1: 0,
          x2: fieldWidth,
          y1: 0,
          y2: fieldHeight,
        },
        additional_events: {},
        coords_start: [0, 0],
        coords_finish: [0, 0],
        current_frame: startTimeFrame,
        event_end: endTimeStamp,
        player: null,
        video_end: videoEndTimeStamp,
        video_start: videoStartTimeStamp,
        training_part_id: trainingPartData?.id || null,
      },
      success: true,
      super_track: {
        super_track_uid: superTrackUid ?? supertrackUids[0],
        kind: "right_team",
      },
      timestamp: startTimeStamp,
    });
    setEditing(currentTime);
    setIsAdding(currentTime);
    setStartTime(null);
    setEndTime(null);
  };

  const onStageScaleChange = (newStageScale) => {
    setStageScale(newStageScale);
  };

  const onStagePosChange = (newStageX, newStageY) => {
    setStageX(newStageX);
    setStageY(newStageY);
  };

  // this is called when drawing new rect
  const updateArrowRect = () => {
    const node = newArrowRef.current;
    node.setAttrs({
      visible: newArrow.current.visible,
      points: [
        (newArrow.current.x1 - stageX) / stageScale,
        (newArrow.current.y1 - stageY) / stageScale,
        (newArrow.current.x2 - stageX) / stageScale,
        (newArrow.current.y2 - stageY) / stageScale,
      ],
      stroke: "blue",
      fill: "blue",
      strokeWidth: 1 / stageScale,
      opacity: 0.6,
    });
    node.getLayer().batchDraw();
  };

  const update2DArrowRect = () => {
    const node = new2DArrowRef.current;
    node.setAttrs({
      visible: new2DArrow.current.visible,
      points: [
        (new2DArrow.current.x1 - stageX) / stageScale,
        (new2DArrow.current.y1 - stageY) / stageScale,
        (new2DArrow.current.x2 - stageX) / stageScale,
        (new2DArrow.current.y2 - stageY) / stageScale,
      ],
      stroke: "blue",
      fill: "blue",
      strokeWidth: 1 / stageScale,
      opacity: 0.6,
    });
    node.getLayer().batchDraw();
  };

  const onMouseDownHandler = (e) => {
    if (e.evt.button === 0 && e.evt.ctrlKey) return; // dragging with Ctrl+LMB
    if (e.evt.button === 2) return; // RMB

    // This part is for selecting a player
    if (["rect"].includes(e.target?.attrs.name)) {
      let { trackId } = e.target.attrs;

      if (!isManaging) {
        console.log("Not managing mode - setting selectedTrackId:", trackId);
        setSelectedTrackId(trackId);
      } else {
        console.log("Managing mode - current state:", {
          selectionMode,
          superTrackUid,
          selectedTrackId,
          clickedTrackId: trackId,
        });

        if (selectionMode === "active") {
          // First click: select active player
          console.log("Setting active player (superTrackUid):", trackId);

          if (trackId === selectedTrackId) {
            // Clicking the same passive player - deselect it
            console.log(
              "Same active player clicked - deselecting active player"
            );
            setSelectedTrackId(null);
            setSelectionMode("passive");
            // toast.info("Passive player deselected.");
          } else {
            // Different player - select as active
            console.log(
              "Different player clicked - setting as new active player:",
              trackId
            );
            setSuperTrackUid(trackId);
            setSelectionMode("passive");
            // toast.success(
            //   `Active player selected: ${trackId}. Click another player to select passive player, or click the same player again to deselect.`
            // );
            console.log(
              "Active player selected:",
              trackId,
              "- switching to passive mode"
            );
          }
        } else {
          // Second click: select passive player
          console.log(
            "In passive mode - comparing clicked trackId:",
            trackId,
            "with superTrackUid:",
            superTrackUid
          );
          if (trackId === superTrackUid) {
            // Clicking the same active player - deselect and reset to active mode
            console.log(
              "Same active player clicked - deselecting active player and resetting to active mode"
            );
            setSelectedTrackId(null);
            setSuperTrackUid(null);
            setSelectionMode("active");
            // toast.info(
            //   "Active player deselected. Click a player to select active player."
            // );
          } else {
            // Different player - select as passive
            console.log(
              "Different player clicked - setting as passive player:",
              trackId
            );
            setSelectedTrackId(trackId);
            setSelectionMode("active");
            // toast.success(
            //   `Passive player selected: ${trackId}. Click a player to select new active player.`
            // );
            console.log(
              "Passive player selected:",
              trackId,
              "- switching back to active mode"
            );
          }
        }
      }
      return;
    }

    if (!drawing) return;

    setIsDrawing(true);
    // start drawing arrow
    const pos = e.target.getStage().getPointerPosition();
    newArrow.current.visible = true;
    newArrow.current.x1 = pos.x;
    newArrow.current.y1 = pos.y;
    newArrow.current.x2 = pos.x;
    newArrow.current.y2 = pos.y;
    new2DArrow.current.visible = false;
    updateArrowRect();
    update2DArrowRect();
  };

  const onMouseMoveHandler = (e) => {
    if (e.evt.button === 2) return;
    if (!newArrow.current.visible || !isDrawing) {
      return;
    }
    const pos = e.target.getStage().getPointerPosition();
    newArrow.current.x2 = pos.x;
    newArrow.current.y2 = pos.y;
    updateArrowRect();
  };

  const onMouseUpHandler = async (e) => {
    try {
      if (!isDrawing) {
        return;
      }
      // newArrow.current.visible = false;
      updateArrowRect();
      setIsDrawing(false);

      const eventId = editing;
      const [x1, y1] = await matchApi.get2Dfrom3D(videoId, [
        (newArrow.current.x1 - stageX) / stageScale / ratio,
        (newArrow.current.y1 - stageY) / stageScale / ratio - initialY,
      ]);
      const [x2, y2] = await matchApi.get2Dfrom3D(videoId, [
        (newArrow.current.x2 - stageX) / stageScale / ratio,
        (newArrow.current.y2 - stageY) / stageScale / ratio - initialY,
      ]);
      updateData(null, "X1", Number(eventId), null, x1);
      updateData(null, "Y1", Number(eventId), null, y1);
      updateData(null, "X2", Number(eventId), null, x2);
      updateData(null, "Y2", Number(eventId), null, y2);
    } catch (err) {}
  };

  const onRatioChange = (ratio) => {
    setRatio(ratio);
  };

  const onSetInitialY = (y) => {
    setInitialY(y);
  };

  const updateStartTime = () => {
    const eventId = editing;
    updateData(
      null,
      "Start Time",
      Number(eventId),
      null,
      frameToTime(Math.round(videoRef.current.currentTime * fps), fps)
    );
  };

  const updateEndTime = () => {
    const eventId = editing;
    updateData(
      null,
      "End Time",
      Number(eventId),
      null,
      frameToTime(Math.round(videoRef.current.currentTime * fps), fps)
    );
  };

  const showSubEventsList = (visibleSubEvents, onSelectEvent, eventType) => {
    const videoPlayerElement = document.querySelector(
      ".video-player-container"
    );
    let position = { x: 10, y: 10 };

    if (videoPlayerElement) {
      const rect = videoPlayerElement.getBoundingClientRect();
      position = {
        x: 10,
        y: rect.height / 2,
      };
    }

    setSubEventsListData({
      isOpen: true,
      visibleSubEvents,
      onSelectEvent,
      position,
      eventType,
    });
  };

  const closeSubEventsList = () => {
    if (editing) {
      // Reset any temporary sub-event changes
      const targetEvent = tableEvents.find(({ id }) => id === Number(editing));
      if (targetEvent) {
        const currentSubEvents = targetEvent.extra.additional_events || {};
        // Update the cell data with original values
        tableMeta?.updateData(
          null,
          "additional_events",
          Number(editing),
          null,
          currentSubEvents
        );
      }
    }
    setSubEventsListData((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const setStart = () => {
    if (!editing) {
      setStartTime(videoRef.current.currentTime);
    } else {
      updateStartTime();
    }
  };

  const setEnd = () => {
    if (!editing) {
      setEndTime(videoRef.current.currentTime);
    } else {
      updateEndTime();
    }
  };

  const deleteEvent = useCallback(
    async (eventId) => {
      try {
        if (eventId === isAdding) {
          setEvents((prevEvents) => [
            ...prevEvents.filter(({ id }) => id !== isAdding),
          ]);
          return;
        }
        const { id, event_type } = events.find(({ id }) => id === eventId);
        const result = window.confirm(
          `Are you sure you want delete ${event_type} event?`
        );

        if (result) {
          await markupApi.deleteEvent(id);
          setEvents((prevEvents) => [
            ...prevEvents.filter(({ id: eventId }) => eventId !== id),
          ]);
          setCurrentEvents((prevEvents) => [
            ...prevEvents.filter(({ id: eventId }) => eventId !== id),
          ]);
          setRealCurrentEvents((prevEvents) => [
            ...prevEvents.filter(({ id: eventId }) => eventId !== id),
          ]);
          toast.success(`Successfully deleted the event ${id}`);
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Something went wrong.";
        toast.error(errorMessage);
      }
    },
    [events, isAdding]
  );

  const updateData = useCallback(
    (rowIndex, columnId, eventId, fieldName, value) => {
      let targetEvent;
      if (isAdding) targetEvent = { ...newEvent };
      else targetEvent = events.find(({ id }) => id === eventId);

      if (!targetEvent) {
        console.log("No Target Event");
        return;
      }
      switch (columnId) {
        case "super_track": {
          const st = matchSuperTracks[value];
          if (!st) break;

          const kind = matchSuperTracks[value]?.kind;
          targetEvent.super_track = {
            ...targetEvent.super_track,
            id: st.id,
            super_track_uid: value,
            kind,
          };
          break;
        }
        case "extra": {
          if (value === null) {
            targetEvent.extra = {
              ...targetEvent.extra,
              player: value,
            };
            break;
          }
          const st = matchSuperTracks[value];
          if (!st) break;
          targetEvent.extra = {
            ...targetEvent.extra,
            player: value,
          };
          break;
        }
        case "success":
          targetEvent.success = value === "true" ? true : false;
          break;
        case "Start Time": {
          targetEvent.timestamp = value;
          targetEvent.extra.current_frame = convertTime2Frame(value, fps);
          const newFrame = targetEvent.extra.current_frame - fps * 3;
          const newVideoStart = frameToTime(newFrame >= 0 ? newFrame : 0, fps);
          targetEvent.extra.video_start = newVideoStart;
          break;
        }
        case "End Time": {
          targetEvent.extra.event_end = value;
          const newFrame = convertTime2Frame(value, fps) + fps * 2;
          const newVideoEnd = frameToTime(newFrame, fps);
          targetEvent.extra.video_end = newVideoEnd;
          break;
        }
        case "X1":
          if (!targetEvent.extra.coords_start)
            targetEvent.extra.coords_start = [0, 0];
          targetEvent.extra.coords_start[0] = Number(value);
          break;
        case "Y1":
          if (!targetEvent.extra.coords_start)
            targetEvent.extra.coords_start = [0, 0];
          targetEvent.extra.coords_start[1] = Number(value);
          break;
        case "X2":
          if (!targetEvent.extra.coords_finish)
            targetEvent.extra.coords_finish = [0, 0];
          targetEvent.extra.coords_finish[0] = Number(value);
          break;
        case "Y2":
          if (!targetEvent.extra.coords_finish)
            targetEvent.extra.coords_finish = [0, 0];
          targetEvent.extra.coords_finish[1] = Number(value);
          break;
        case "additional_events":
          targetEvent.extra.additional_events = value;
          break;
        case "event_type":
          targetEvent.event_type = value;
          targetEvent.extra.additional_events = {};
          break;
        default:
          targetEvent[columnId] = value;
      }
      if (isAdding) setNewEvent(targetEvent);
      else setEvents([...events]);
    },
    [events, matchSuperTracks, isAdding, newEvent]
  );

  const updateEvent = useCallback(
    async (eventId) => {
      try {
        if (isAdding) {
          const {
            id,
            highlighted,
            event_length,
            super_track: { super_track_uid, kind },
            ...data
          } = newEvent;

          const { super_track_id, ...createdEvent } = await markupApi.addEvent(
            videoId,
            super_track_uid,
            {
              ...data,
            }
          );

          const sortedEvents = [
            {
              ...createdEvent,
              super_track: {
                super_track_uid,
                kind,
                id: super_track_id,
              },
            },
            ...events,
          ].sort((a, b) => a?.extra.current_frame - b?.extra.current_frame);
          setEvents(sortedEvents);

          setNewEvent(null);
          setIsAdding(false);
          toast.success("Successfully added new event");
        } else {
          const {
            id,
            highlighted,
            event_length,
            super_track: { id: supertrackId },
            ...data
          } = events.find(({ id }) => id === eventId);

          await markupApi.updateEvent(id, {
            ...data,
            super_track: supertrackId,
          });
          toast.success(`Successfully updated the event ${id}`);
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Something went wrong while updating event.";
        toast.error(errorMessage);
      }
    },
    [events, isAdding, newEvent]
  );

  // Add these functions to navigate between intervals
  const goToNextInterval = useCallback(() => {
    if (intervals && intervals.length > 0 && videoRef.current) {
      const currentFrame = Math.round(videoRef.current.currentTime * fps);
      let nextIntervalStart = null;

      for (const [start, end] of intervals) {
        if (
          start > currentFrame &&
          (nextIntervalStart === null || start < nextIntervalStart)
        ) {
          nextIntervalStart = start;
        }
      }

      if (nextIntervalStart !== null) {
        videoRef.current.currentTime = nextIntervalStart / fps;
        console.log(`Jumped to next interval at frame ${nextIntervalStart}`);
      } else {
        console.log("No next interval found");
      }
    }
  }, [intervals, fps]);

  const goToPreviousInterval = useCallback(() => {
    if (intervals && intervals.length > 0 && videoRef.current) {
      const currentFrame = Math.round(videoRef.current.currentTime * fps);
      let prevIntervalIndex = -1;

      // Sort intervals by start time to ensure proper ordering
      const sortedIntervals = [...intervals].sort((a, b) => a[0] - b[0]);

      // Find the previous interval (the last interval that starts before current time)
      for (let i = sortedIntervals.length - 1; i >= 0; i--) {
        const [start, end] = sortedIntervals[i];

        // If we're currently in this interval, look for the previous one
        if (currentFrame >= start && currentFrame <= end) {
          prevIntervalIndex = i - 1;
          break;
        }

        // If this interval is entirely before current position, it's our target
        if (end < currentFrame) {
          prevIntervalIndex = i;
          break;
        }
      }

      if (prevIntervalIndex >= 0) {
        const [start, _] = sortedIntervals[prevIntervalIndex];
        videoRef.current.currentTime = start / fps;
        console.log(`Jumped to previous interval at frame ${start}`);
      } else {
        console.log("No previous interval found");
      }
    }
  }, [intervals, fps]);

  const handleOpponentTeamClick = useCallback(() => {
    if (!opponentsSuperTrack) return;
    console.log("selection mode", selectionMode);
    if (selectionMode === "active") {
      setSuperTrackUid(opponentsSuperTrack.uid);
      setSelectionMode("passive");
    } else {
      setSelectedTrackId(opponentsSuperTrack.uid);
      setSelectionMode("active");
    }
  }, [opponentsSuperTrack, selectionMode]);

  const tableMeta = {
    editingId: editing,
    setEditingEventId: setEditing,
    previewEventId,
    setPreviewEventId,
    deleteEvent,
    updateData,
    updateEvent,
    showSubEventsList,
    closeSubEventsList,
    setSubEventsListData,
  };

  useEffect(() => {
    // Make videoRef accessible to EventControls
    window.videoRef = videoRef;

    const handleKeyPress = (e) => {
      // Handle 'S' key for start time
      if (e.key.toLowerCase() === "s") {
        setStart();
      }
      // Handle 'G' key for end time
      if (e.key.toLowerCase() === "g") {
        setEnd();
      }

      // Handle 'Q' key for previous interval
      if (e.key.toLowerCase() === "q") {
        goToPreviousInterval();
      }

      // Handle 'W' key for next interval
      if (e.key.toLowerCase() === "w") {
        goToNextInterval();
      }

      // Handle 'E' key to toggle Watch More mode
      if (e.key.toLowerCase() === "e") {
        setWatchMoreMode((prev) => !prev);
      }

      // Handle 'R' key to toggle Auto Zoom
      if (e.key.toLowerCase() === "r") {
        setAutoZoom((prev) => !prev);
      }

      if (e.key.toLowerCase() === "o" && isSingleTeam && opponentsSuperTrack) {
        handleOpponentTeamClick();
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      delete window.videoRef;
    };
  }, [
    editing,
    newEvent,
    isAdding,
    intervals,
    fps,
    isSingleTeam,
    opponentsSuperTrack,
    selectionMode,
  ]);

  useEffect(() => {
    if (isAdding) {
      const tableEvents = showAll
        ? [...events]
        : previewEvent
        ? [previewEvent]
        : [...currentEvents];
      if (newEvent) tableEvents.unshift(newEvent);
      setTableEvents(tableEvents);
      return;
    }
    if (editing && !showAll) return;

    const tableEvents = showAll
      ? [...events]
      : previewEvent
      ? [previewEvent]
      : [...currentEvents];
    if (newEvent) tableEvents.unshift(newEvent);
    setTableEvents(tableEvents);
  }, [showAll, previewEvent, events, currentEvents, newEvent]);

  useEffect(() => {
    async function fetchEvents() {
      try {
        // if (!trackId) return;

        // setSuperTrackUid(trackId);

        setIsLoading(true);
        let data = await matchApi.getMatch(videoId);
        setFps(data.video.fps);
        setVideoUrl(data.video.origin);

        // Fetch task Detail
        const query = new URLSearchParams(location.search);
        const taskId = query.get("taskId");
        if (taskId) {
          setTaskId(taskId);
          const response = await matchApi.getMatchTask(videoId, taskId);
          const {
            intervals,
            frame_start,
            frame_end,
            pipeline_type,
            training_part_id,
          } = response.task;
          const pipelineType =
            pipeline_type === "training_analytics"
              ? "team_analytics_pro"
              : pipeline_type;

          setPipelineType(pipelineType);
          if (
            ![
              "team_analytics_pro",
              "team_analytics_pro_tagging",
              "advanced",
              "advanced_draw",
            ].includes(pipelineType)
          ) {
            setLightMode(true);
          }

          if (intervals && Array.isArray(intervals)) {
            const [frame_start, frame_end] = intervals[0];
            const newQuery = new URLSearchParams(location.search);
            newQuery.set("frame_start", frame_start);
            newQuery.set("frame_end", frame_end);
            navigate(`${location.pathname}?${newQuery.toString()}`, {
              replace: true,
            });
            setVideoStartTime(frame_start / data.video.fps);
            setVideoEndTime(frame_end / data.video.fps);
          }

          if (frame_start != null && frame_end != null) {
            const newQuery = new URLSearchParams(location.search);
            newQuery.set("frame_start", frame_start);
            newQuery.set("frame_end", frame_end);
            console.log({ frame_start, frame_end });
            navigate(`${location.pathname}?${newQuery.toString()}`, {
              replace: true,
            });
            setVideoStartTime(frame_start / data.video.fps);
            setVideoEndTime(frame_end / data.video.fps);
          }

          setSelectionMode("active");
          let teamsAmount = response.task?.teams_amount;
          if(pipeline_type === "training_analytics") {
            teamsAmount = 2;
          }
          setTeamsAmount(teamsAmount);
          setIsSingleTeam(teamsAmount === 1);
          setOpponentsSuperTrack({
            id: response.task?.opponents_super_track_id,
            uid: response.task?.opponents_super_track_uid,
          });

          if (pipeline_type === "training_analytics" && training_part_id > 0) {
            try {
              const trainingPartResponse = await matchApi.getTrainingPart(
                videoId,
                training_part_id
              );
              console.log("trainingPartResponse", trainingPartResponse);
              setTrainingPartData(trainingPartResponse);

              // Convert zone to zoom area if available
              if (trainingPartResponse.zone) {
                const zoomArea = convertZoneToZoomArea(
                  trainingPartResponse.zone
                );
                setTrainingZoomArea(zoomArea);
              }
            } catch (error) {
              console.error("Failed to fetch training part data:", error);
              toast.error("Failed to load training part details");
            }
          }
        } else {
          console.log("No taskId found - setting isManaging to true");
          setIsManaging(true);
        }

        let events = await matchApi.getEvents({ matchId: videoId });
        events = events.sort(
          (a, b) => a?.extra.current_frame - b?.extra.current_frame
        );
        setEvents(events);

        const {
          field: { width, height },
        } = await markupApi.getFieldMeta(videoId);
        setFieldWidth(width);
        setFieldHeight(height);

        const { frame_step, supertracks, tracks } =
          await matchApi.getMatchSuperTracksFull(videoId);

        if (!supertracks || !tracks) return;

        setMatchSuperTracks(supertracks);

        const newTracks = {};
        const uids = [];
        for (const supertrack_uid in supertracks) {
          uids.push(supertrack_uid);
          for (const track of supertracks[supertrack_uid].tracks) {
            const { track_id } = track;
            newTracks[supertrack_uid] = {
              ...newTracks[supertrack_uid],
              ...tracks[track_id],
            };
          }
        }
        setSupertracks(newTracks);
        setSupertrackUids(uids);
        // setActiveTracks([super_track_uid]);

        if (frame_step) setFrameStep(frame_step);

        // Get Task Detail
        // const { task } = await matchApi.postMatchTask(videoId, "validate");
        // if (!task) return;
        // const { frame_start, frame_stop_id } = task;
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          JSON.stringify(err.response?.data)?.slice(0, 200) ||
          err.message ||
          "Something went wrong.";
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
    async function fetchSubEvents() {
      const subEvents = await eventsApi.getSubEvents();
      setSubEvents(subEvents);
    }
    fetchEvents();
    fetchSubEvents();
  }, [trackId]);

  // Update zoom area when training part data changes
  useEffect(() => {
    if (trainingPartData?.zone) {
      const zoomArea = convertZoneToZoomArea(trainingPartData.zone);
      console.log("Training part zone:", trainingPartData.zone);
      console.log("Converted zoom area:", zoomArea);
      setTrainingZoomArea(zoomArea);
    } else {
      setTrainingZoomArea(null);
    }
  }, [trainingPartData, convertZoneToZoomArea]);

  useEffect(() => {
    if (!fps) return;

    if (previewEventId === null) {
      const query = new URLSearchParams(location.search);
      const frame_start = query.get("frame_start");
      const frame_stop_id = query.get("frame_stop_id");
      if (frame_start !== null && frame_stop_id !== null) {
        setVideoStartTime(frame_start / fps);
        setVideoEndTime(frame_stop_id / fps);
      } else {
        setVideoStartTime(null);
        setVideoEndTime(null);
      }
      setPreviewEvent(null);
    } else {
      const targetEvent = events.find(({ id }) => previewEventId === id);
      if (!targetEvent) return;
      setPreviewEvent(targetEvent);

      const startTime = convertTime2Frame(targetEvent.extra.video_start, fps);
      const endTime = convertTime2Frame(targetEvent.extra.video_end, fps);
      setVideoStartTime(startTime / fps);
      setVideoEndTime(endTime / fps);
    }
  }, [previewEventId, fps]);

  useEffect(() => {
    if (editing) {
      const eventId = editing;
      const targetEvent = tableEvents.find(({ id }) => id === Number(eventId));
      setEditingEvent(targetEvent);
    } else {
      setEditing(false);
      setEditingEvent(null);
    }
  }, [editing]);

  useEffect(() => {
    // if (!previewEvent) return;

    const events = previewEvent ? [previewEvent] : realCurrentEvents;
    let newTracks = [];
    for (const event of events) {
      if (!event.super_track) continue;
      const { super_track_uid, tracks_data } = event.super_track;
      newTracks = [...newTracks, String(super_track_uid)];
    }
    setActiveTracks(newTracks);
  }, [realCurrentEvents, previewEvent]);

  return (
    <>
      {!isLoading ? (
        <Container className="py-3 event-validation-container" fluid>
          <div className="tw-flex tw-justify-center tw-gap-1 tw-flex-col sm:tw-flex-row tw-z-10">
            <div className="sm:tw-w-2/3 tw-w-full tw-flex tw-relative">
              <VideoPlayer
                className="tw-w-full tw-relative"
                src={videoUrl}
                fps={fps}
                ref={videoRef}
                videoId={videoId}
                videoProps={{
                  autoPlay: false,
                  loop: false,
                }}
                frameStep={frameStep}
                autoZoom={autoZoom}
                focusedTrackId={superTrackUid}
                shouldFocusOnBall={true}
                tracks={supertracks}
                startTime={watchMoreMode ? null : videoStartTime}
                endTime={watchMoreMode ? null : videoEndTime}
                activeTracks={
                  showBoxes
                    ? null
                    : (showAll && !previewEvent) || lightMode
                    ? null
                    : eventSelected
                    ? null
                    : activeTracks
                }
                // activeTracks={showAll && !previewEvent ? null : activeTracks}
                // coloring players
                colors={{
                  [superTrackUid]: "yellow",
                  [selectedTrackId]: "red",
                }}
                zoomArea={trainingZoomArea}
                onFrameChange={onFrameChange}
                onVideoDomSizeChange={onVideoDomSizeChange}
                onMouseDownHandler={onMouseDownHandler}
                onMouseMoveHandler={onMouseMoveHandler}
                onMouseUpHandler={onMouseUpHandler}
                onStageScaleChange={onStageScaleChange}
                onStagePosChange={onStagePosChange}
                onRatioChange={onRatioChange}
                onSetInitialY={onSetInitialY}
              >
                <Arrow ref={newArrowRef} name="newArrow" />
              </VideoPlayer>

              <div
                className="tw-flex tw-justify-center tw-gap-2"
                style={{
                  position: "absolute",
                  top: `${videoDomHeight - 100}px`,
                  left: 0,
                  right: 0,
                  color: "white",
                }}
              >
                {realCurrentEvents.map(({ id, event_type }) => (
                  <span key={id}>{event_type}</span>
                ))}
              </div>

              <div className="tw-absolute tw-left-4 tw-bottom-12 tw-flex tw-flex-col tw-justify-center tw-items-start tw-gap-2">
                <div className="">
                  <EventControls
                    lightMode={lightMode}
                    isSingleTeam={isSingleTeam || lightMode}
                    pipelineType={pipelineType}
                    addNewEvent={addNewEvent}
                    onEventSelected={setEventSelected}
                  />
                </div>
              </div>
              <div className="tw-absolute tw-right-4 tw-bottom-12 tw-flex tw-flex-col tw-justify-center tw-items-start tw-gap-2">
                {isSingleTeam && opponentsSuperTrack && (
                  <EventButton
                    label="Opp.team"
                    number="O"
                    onClick={handleOpponentTeamClick}
                  />
                )}
              </div>
            </div>
          </div>
          <div
            className="d-flex flex-column tw-gap-1 events-showing-section tw-z-30 tw-relative"
            xs={12}
            xl={6}
          >
            <div className="tw-flex tw-justify-between tw-items-center">
              <div className="tw-flex tw-gap-2 tw-items-center">
                <Switch
                  className=""
                  label="Show All"
                  checked={showAll}
                  onChange={setShowAll}
                />
                <Switch
                  label="Show Boxes"
                  checked={showBoxes}
                  onChange={setShowBoxes}
                />
                <Switch
                  label="Auto Zoom (R)"
                  checked={autoZoom}
                  onChange={setAutoZoom}
                />
                <Switch
                  label="Watch More (E)"
                  checked={watchMoreMode}
                  onChange={setWatchMoreMode}
                />
                <Button variant="success" size="sm" onClick={setStart}>
                  Start
                </Button>
                <Button variant="success" size="sm" onClick={setEnd}>
                  End
                </Button>
                <Button
                  className="add-new-event-btn"
                  variant="success"
                  size="sm"
                  onClick={handleAddNewEvent}
                >
                  Add New Event
                </Button>
                <span>Prev: Q</span>
                <span>Next: W</span>
                {isManaging && (
                  <>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        console.log(
                          "Resetting players - clearing all selections"
                        );
                        console.log("Before reset:", {
                          superTrackUid,
                          selectedTrackId,
                          selectionMode,
                        });
                        setSuperTrackUid(null);
                        setSelectedTrackId(null);
                        setSelectionMode("active");
                        console.log(
                          "After reset: superTrackUid=null, selectedTrackId=null, selectionMode=active"
                        );
                        toast.info("Player selections cleared.");
                      }}
                    >
                      Reset Players
                    </Button>
                    <div className="tw-flex tw-gap-2 tw-items-center tw-px-2 tw-py-1 tw-bg-gray-100 tw-rounded">
                      <span className="tw-text-sm tw-font-medium">
                        Mode:{" "}
                        {selectionMode === "active"
                          ? "Select Active Player"
                          : "Select Passive Player"}
                      </span>
                      {superTrackUid && (
                        <span className="tw-text-sm tw-bg-yellow-200 tw-px-2 tw-py-1 tw-rounded">
                          Active: {superTrackUid}
                        </span>
                      )}
                      {selectedTrackId && (
                        <span className="tw-text-sm tw-bg-red-200 tw-px-2 tw-py-1 tw-rounded">
                          Passive: {selectedTrackId}
                        </span>
                      )}
                    </div>
                  </>
                )}
                {startTime !== null && <span>Start Time: {startTime}</span>}
                {endTime !== null && <span>End Time: {endTime}</span>}
              </div>
              <div className="tw-flex tw-gap-2 tw-justify-end">
                {previewEventId !== null && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={turnOffPreviewMode}
                  >
                    <FontAwesomeIcon icon={["fas", "eye-slash"]} /> Turn Off
                    Preview Mode
                  </Button>
                )}
                {taskId && (
                  <Button
                    variant="success"
                    size="sm"
                    disabled={isSaving}
                    onClick={saveAnswer}
                  >
                    {isSaving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />{" "}
                        Saving
                      </>
                    ) : (
                      "Save Answer"
                    )}
                  </Button>
                )}
              </div>
            </div>
            <div
              className="event-list-table overflow-auto flex-grow-1 position-relative"
              ref={tableRef}
            >
              <EventsTable
                columns={columns}
                tableEvents={tableEvents}
                editingId={editing}
                setEditingEventId={setEditing}
                previewEventId={previewEventId}
                setPreviewEventId={setPreviewEventId}
                deleteEvent={deleteEvent}
                updateData={updateData}
                updateEvent={updateEvent}
                showSubEventsList={showSubEventsList}
                setSubEventsListData={setSubEventsListData}
              />
            </div>
          </div>
        </Container>
      ) : (
        <Preloader className="m-auto" />
      )}
    </>
  );
};

export default Events;
